import { useCallback, useRef, useState, useSyncExternalStore } from "react";
import { tryJsonParse } from "$root/utils";
import { userStorage } from "./userStorage";
import { identity } from "@mdotm/utils/functions";
import { useUnsafeUpdatedRef } from "@mdotm/utils/react-extra";

export function useUserStorage(key: string): [string | null | undefined, (value: string | null | undefined) => void] {
	const value = useSyncExternalStore(
		(callback) => userStorage.subscribe(key, callback),
		() => userStorage.get(key),
	);

	const setValue = useCallback(
		(newValue: string | null | undefined) => {
			if (newValue == null) {
				userStorage.delete(key);
			} else {
				userStorage.set(key, newValue);
			}
		},
		[key],
	);

	return [value, setValue];
}

export function useUserStorageJson<T>(
	key: string,
	fallback: T,
	opts?: {
		fromPrimitives?: (val: object | null) => T;
		/** Can throw to indicate that T cannot be reconstructed by the parsed JSON found in the storage area */
		toPrimitives?: (val: T) => object | null;
	},
): [T, (value: T) => void] {
	const [raw, setRaw] = useUserStorage(key);

	const [value, _setValue] = useState<T>(() => valueFromRaw(raw, fallback, opts?.fromPrimitives));

	const optsRef = useUnsafeUpdatedRef(opts);
	const oldRawRef = useRef(raw);
	const setValue = useCallback(
		(newValue: T) => {
			const newRaw = newValue == null ? null : JSON.stringify((optsRef.current?.toPrimitives ?? identity)(newValue));
			setRaw(newRaw);
			oldRawRef.current = newRaw;
			_setValue(newValue);
		},
		[optsRef, setRaw],
	);

	if (oldRawRef.current !== raw) {
		oldRawRef.current = raw;
		_setValue(valueFromRaw(raw, fallback, opts?.fromPrimitives));
	}

	return [value, setValue];
}

function valueFromRaw<T>(raw: string | null | undefined, fallback: T, fromPrimitives?: (val: object | null) => T): T {
	if (raw == null) return fallback;
	return tryJsonParse<T>(raw, fallback, fromPrimitives);
}
